import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Input, Modal, Form, Tag, Upload,
  message, Popconfirm, Select, Tabs, List as AntList,
} from 'antd';
import {
  PlusOutlined, UploadOutlined, SearchOutlined, EditOutlined,
  DeleteOutlined, TeamOutlined,
} from '@ant-design/icons';
import { contactsAPI } from '../services/api';

const { TabPane } = Tabs;

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form] = Form.useForm();

  // Contact Lists
  const [contactLists, setContactLists] = useState([]);
  const [listModalOpen, setListModalOpen] = useState(false);
  const [listForm] = Form.useForm();
  const [selectedList, setSelectedList] = useState(null);
  const [listDetailOpen, setListDetailOpen] = useState(false);
  const [listContacts, setListContacts] = useState([]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await contactsAPI.list(params);
      setContacts(res.data);
    } catch (e) {
      message.error('載入聯絡人失敗');
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchLists = useCallback(async () => {
    try {
      const res = await contactsAPI.listLists();
      setContactLists(res.data);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => { fetchLists(); }, [fetchLists]);

  const handleCreate = () => {
    setEditingContact(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingContact(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingContact) {
        await contactsAPI.update(editingContact.id, values);
        message.success('已更新聯絡人');
      } else {
        await contactsAPI.create(values);
        message.success('已新增聯絡人');
      }
      setModalOpen(false);
      fetchContacts();
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || '操作失敗');
    }
  };

  const handleDelete = async (id) => {
    try {
      await contactsAPI.delete(id);
      message.success('已刪除');
      fetchContacts();
    } catch (e) {
      message.error('刪除失敗');
    }
  };

  const handleImport = async (info) => {
    try {
      const res = await contactsAPI.import(info.file);
      const data = res.data;
      message.success(`匯入完成: ${data.imported} 筆成功, ${data.skipped} 筆略過`);
      if (data.errors && data.errors.length > 0) {
        message.warning(`${data.errors.length} 筆錯誤`);
      }
      fetchContacts();
    } catch (e) {
      message.error('匯入失敗: ' + (e.message || ''));
    }
  };

  // List functions
  const handleCreateList = async () => {
    try {
      const values = await listForm.validateFields();
      await contactsAPI.createList(values);
      message.success('已建立名單');
      setListModalOpen(false);
      listForm.resetFields();
      fetchLists();
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.message || '建立失敗');
    }
  };

  const handleViewList = async (list) => {
    try {
      const res = await contactsAPI.getList(list.id);
      setSelectedList(res.data);
      setListContacts(res.data.contacts || []);
      setListDetailOpen(true);
    } catch (e) {
      message.error('載入名單失敗');
    }
  };

  const handleRemoveMember = async (contactId) => {
    if (!selectedList) return;
    try {
      await contactsAPI.removeMember(selectedList.id, contactId);
      message.success('已移除');
      handleViewList(selectedList);
    } catch (e) {
      message.error('移除失敗');
    }
  };

  const columns = [
    { title: '姓名', dataIndex: 'name', key: 'name', width: 120 },
    { title: 'Email', dataIndex: 'email', key: 'email', width: 200 },
    { title: '公司', dataIndex: 'company_name', key: 'company_name', width: 160 },
    { title: '電話', dataIndex: 'phone', key: 'phone', width: 130 },
    {
      title: '標籤', dataIndex: 'tags', key: 'tags', width: 150,
      render: (tags) => tags && tags.length > 0
        ? tags.map(t => <Tag key={t} color="blue">{t}</Tag>)
        : '-',
    },
    {
      title: '來源', dataIndex: 'source', key: 'source', width: 80,
      render: (s) => <Tag>{s || 'import'}</Tag>,
    },
    {
      title: '建立日期', dataIndex: 'created_at', key: 'created_at', width: 120,
      render: (v) => v ? new Date(v).toLocaleDateString('zh-TW') : '-',
    },
    {
      title: '操作', key: 'actions', width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="確定刪除?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Tabs defaultActiveKey="contacts">
        <TabPane tab="聯絡人" key="contacts">
          <Card
            title="聯絡人管理"
            extra={
              <Space>
                <Input
                  placeholder="搜尋姓名/Email/公司"
                  prefix={<SearchOutlined />}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onPressEnter={fetchContacts}
                  style={{ width: 220 }}
                  allowClear
                />
                <Upload
                  accept=".xlsx,.xls"
                  showUploadList={false}
                  customRequest={({ file }) => handleImport({ file })}
                >
                  <Button icon={<UploadOutlined />}>匯入 Excel</Button>
                </Upload>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                  新增聯絡人
                </Button>
              </Space>
            }
          >
            <Table
              dataSource={contacts}
              columns={columns}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1000 }}
              pagination={{ pageSize: 20, showSizeChanger: true }}
            />
          </Card>
        </TabPane>

        <TabPane tab="聯絡人名單" key="lists">
          <Card
            title="聯絡人名單"
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { listForm.resetFields(); setListModalOpen(true); }}>
                建立名單
              </Button>
            }
          >
            <Table
              dataSource={contactLists}
              rowKey="id"
              columns={[
                { title: '名單名稱', dataIndex: 'name', key: 'name' },
                { title: '說明', dataIndex: 'description', key: 'description' },
                { title: '成員數', dataIndex: 'member_count', key: 'member_count', width: 100 },
                {
                  title: '建立日期', dataIndex: 'created_at', key: 'created_at', width: 120,
                  render: (v) => v ? new Date(v).toLocaleDateString('zh-TW') : '-',
                },
                {
                  title: '操作', key: 'actions', width: 100,
                  render: (_, record) => (
                    <Button type="link" icon={<TeamOutlined />} onClick={() => handleViewList(record)}>
                      查看
                    </Button>
                  ),
                },
              ]}
              pagination={false}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* Create/Edit Contact Modal */}
      <Modal
        title={editingContact ? '編輯聯絡人' : '新增聯絡人'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="儲存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="姓名" rules={[{ required: true, message: '請輸入姓名' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, message: '請輸入 Email' }, { type: 'email', message: '請輸入有效的 Email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="company_name" label="公司">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="電話">
            <Input />
          </Form.Item>
          <Form.Item name="title" label="職稱">
            <Input />
          </Form.Item>
          <Form.Item name="tags" label="標籤">
            <Select mode="tags" placeholder="輸入標籤後按 Enter" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Create List Modal */}
      <Modal
        title="建立聯絡人名單"
        open={listModalOpen}
        onOk={handleCreateList}
        onCancel={() => setListModalOpen(false)}
        okText="建立"
        cancelText="取消"
      >
        <Form form={listForm} layout="vertical">
          <Form.Item name="name" label="名單名稱" rules={[{ required: true, message: '請輸入名單名稱' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="說明">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* List Detail Modal */}
      <Modal
        title={selectedList ? `名單: ${selectedList.name}` : '名單詳情'}
        open={listDetailOpen}
        onCancel={() => setListDetailOpen(false)}
        footer={null}
        width={700}
      >
        <Table
          dataSource={listContacts}
          rowKey="id"
          size="small"
          columns={[
            { title: '姓名', dataIndex: 'name', key: 'name' },
            { title: 'Email', dataIndex: 'email', key: 'email' },
            { title: '公司', dataIndex: 'company_name', key: 'company_name' },
            {
              title: '操作', key: 'actions', width: 80,
              render: (_, record) => (
                <Popconfirm title="確定移除?" onConfirm={() => handleRemoveMember(record.id)}>
                  <Button type="link" danger size="small">移除</Button>
                </Popconfirm>
              ),
            },
          ]}
          pagination={false}
        />
      </Modal>
    </div>
  );
}
